'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import { OnboardingEventKeys, OnboardingEvents } from '../events';
import { EventBus } from '@/shared/utils';

import OnboardingStart from '../ui/onboarding-start';
import InstrumentSelection, { InstrumentSelectionSkeleton } from '../ui/instrument-selection-page';
import InstrumentsChildSelection from '../ui/instruments-child-selection';

import { useOnboarding } from '../hooks/use-onboarding';
import StudySessions from '../ui/study-sessions';
import { Onboarding } from '@/shared/i18n';
import { LevelSelection } from '../ui/level-selection';
import { ProfileSkillLevelSchema, StudySessions as StudySessionsType } from '../types';
import MusicJourneySelection from '../ui/music-journey-selection';

interface OnboardingScreenProps {
  eventBus: EventBus<OnboardingEvents>;
  step?: number;
  dict: Onboarding;
  token?: string;
}

export default function OnboardingScreen({ eventBus, step = 0, token }: OnboardingScreenProps) {
  const [formType, setFormType] = useState<
    | 'start'
    | 'step-instrument-selection'
    | 'step-instrument-child-selection'
    | 'step-study-sessions'
    | 'step-level-selection'
    | 'step-level-selection-child'
  >('start');

  const [loading, setLoading] = useState(true);

  const {
    startOnboarding,
    instrumentSelectionDoHandler,
    instrumentChildDoHandler,
    instrumentsViewHandler,
    instruments: oldInstruments,
    selectedInstrument,
    studySessionsViewHandler,
    studySessionsDoHandler,
    skillLevelsViewHandler,
    skillLevelDoHandler,
    endOnboarding,
  } = useOnboarding(eventBus);

  const instruments = oldInstruments.slice();
  const theme = useTheme();

  function onStartView() {
    startOnboarding();
    setFormType('start');
  }

  function onInstrumentSelectionView() {
    if (!token) {
      return;
    }
    instrumentsViewHandler({ token });
    setFormType('step-instrument-selection');
  }

  /** Arriva quando l’utente clicca “Continua” su InstrumentSelection */
  const onInstrumentSelectionDo = (payload: { instrumentName: string }) => {
    instrumentSelectionDoHandler({ instrumentName: payload.instrumentName });
    // Cerchiamo il parent
    const parent = instruments.find((i) => i.instrumentName === payload.instrumentName);
    if (!parent) {
      // Se non lo troviamo -> step 2
      eventBus.emit(OnboardingEventKeys.studySessions.view, undefined);
      return;
    }

    // Controlliamo se ha child
    const childInstruments = instruments.filter(
      (inst) => inst.parentInstrument?.instrumentName === payload.instrumentName,
    );

    if (childInstruments.length === 0) {
      // Se nessun child -> step 2
      eventBus.emit(OnboardingEventKeys.studySessions.view, undefined);
    } else {
      // Altrimenti -> mostriamo la child selection
      eventBus.emit(OnboardingEventKeys.instrument.child.view, {
        parentName: payload.instrumentName,
      });
    }
  };

  function onStudySessionSelectionDo(payload: StudySessionsType[0]) {
    studySessionsDoHandler(payload);
    eventBus.emit(OnboardingEventKeys.levelSelection.view, undefined);
  }

  function onBoardingEndRedirectDo() {
    eventBus.emit(OnboardingEventKeys.end.redirect, undefined);
  }

  /** Arriva quando dobbiamo mostrare la child selection */
  function onInstrumentChildView(payload: { parentName: string }) {
    setFormType('step-instrument-child-selection');
  }

  /** Arriva quando l’utente clicca “Continua” su InstrumentsChildSelection */
  function onInstrumentChildDo(payload: { childName: string }) {
    instrumentChildDoHandler({ childName: payload.childName });
    // Subito step2
    eventBus.emit(OnboardingEventKeys.instrument.child.done, undefined);
  }

  /** Arriva quando l’utente clicca “Indietro” su InstrumentsChildSelection */
  function onInstrumentChildBack() {
    setFormType('step-instrument-selection');
  }

  function onSessionsView() {
    if (!token) {
      return;
    }
    studySessionsViewHandler({ token });
    console.log('Step 2: Study Sessions');
    setFormType('step-study-sessions');
  }

  function onLevelSelectionView() {
    console.log('Step 3: Level Selection');
    if (!token) {
      return;
    }
    skillLevelsViewHandler({ token });
    setFormType('step-level-selection');
  }

  function onLevelSelectionChildView() {
    if (!token) {
      return;
    }
    skillLevelsViewHandler({ token });
    console.log('Step 3: Level Selection Child');
    setFormType('step-level-selection-child');
  }

  function onLevelSelectionDo(payload: ProfileSkillLevelSchema) {
    skillLevelDoHandler(payload);
  }

  function onBoardingEndDo() {
    if (!token) {
      return;
    }
    endOnboarding({
      token,
    });
  }

  useEffect(() => {
    // Gestione dello stato corrente

    const timeout = setTimeout(() => setLoading(false), 1000);

    eventBus.on(OnboardingEventKeys.start.view, onStartView);

    eventBus.on(OnboardingEventKeys.instrument.selection.view, onInstrumentSelectionView);
    eventBus.on(OnboardingEventKeys.instrument.selection.do, onInstrumentSelectionDo);

    eventBus.on(OnboardingEventKeys.instrument.child.view, onInstrumentChildView);
    eventBus.on(OnboardingEventKeys.instrument.child.do, onInstrumentChildDo);
    eventBus.on(OnboardingEventKeys.instrument.child.back, onInstrumentChildBack);

    eventBus.on(OnboardingEventKeys.studySessions.selection.view, onSessionsView);
    eventBus.on(OnboardingEventKeys.studySessions.selection.do, onStudySessionSelectionDo);

    eventBus.on(OnboardingEventKeys.levelSelection.selection.view, onLevelSelectionView);
    eventBus.on(OnboardingEventKeys.levelSelection.child.view, onLevelSelectionChildView);
    eventBus.on(OnboardingEventKeys.levelSelection.selection.do, onLevelSelectionDo);
    eventBus.on(OnboardingEventKeys.end.do, onBoardingEndDo);
    eventBus.on(OnboardingEventKeys.end.redirectDo, onBoardingEndRedirectDo);

    const stepFlow: { [key: number]: () => void; default: () => void } = {
      0: () => {
        console.log('Esecuzione per step 0');
        eventBus.emit(OnboardingEventKeys.start.view, undefined);
      },
      1: () => {
        console.log('Esecuzione per step 1');
        eventBus.emit(OnboardingEventKeys.instrument.selection.view, undefined);
      },
      2: () => {
        console.log('Esecuzione per step 2');
        eventBus.emit(OnboardingEventKeys.studySessions.selection.view, undefined);
      },
      3: () => {
        console.log('Esecuzione per step 3');
        eventBus.emit(OnboardingEventKeys.levelSelection.selection.view, undefined);
      },
      // Aggiungi altri step se necessario
      default: () => {
        console.warn('Step non riconosciuto, nessuna azione eseguita.');
      },
    };
    // Gestione dello stato corrente
    const handleStep = stepFlow[step] || stepFlow.default;
    handleStep();

    return () => {
      clearTimeout(timeout);
      eventBus.off(OnboardingEventKeys.start.view, onStartView);
      // eventBus.off(OnboardingEventKeys.createProfile, onCreateProfileHandler);
      eventBus.off(OnboardingEventKeys.instrument.selection.view, onInstrumentSelectionView);
      eventBus.off(OnboardingEventKeys.instrument.selection.do, onInstrumentSelectionDo);

      eventBus.off(OnboardingEventKeys.instrument.child.view, onInstrumentChildView);
      eventBus.off(OnboardingEventKeys.instrument.child.do, onInstrumentChildDo);
      eventBus.off(OnboardingEventKeys.instrument.child.back, onInstrumentChildBack);
      eventBus.off(OnboardingEventKeys.studySessions.selection.view, onSessionsView);
      eventBus.off(OnboardingEventKeys.studySessions.selection.do, onStudySessionSelectionDo);

      eventBus.off(OnboardingEventKeys.levelSelection.selection.view, onLevelSelectionView);
      eventBus.off(OnboardingEventKeys.levelSelection.child.view, onLevelSelectionChildView);
      eventBus.off(OnboardingEventKeys.levelSelection.selection.do, onLevelSelectionDo);
      eventBus.off(OnboardingEventKeys.end.do, onBoardingEndDo);
      eventBus.off(OnboardingEventKeys.end.redirectDo, onBoardingEndRedirectDo);
    };
  }, [eventBus]);

  return (
    <>
      {!loading && formType === 'start' && <OnboardingStart eventBus={eventBus} />}

      {loading && formType === 'step-instrument-selection' && <InstrumentSelectionSkeleton />}

      {!loading && formType === 'step-instrument-selection' && (
        <InstrumentSelection eventBus={eventBus} instruments={instruments} theme={theme} />
      )}

      {!loading && formType === 'step-study-sessions' && <StudySessions eventBus={eventBus} />}

      {!loading && formType === 'step-instrument-child-selection' && (
        <InstrumentsChildSelection
          eventBus={eventBus}
          childInstruments={instruments.filter(
            (inst) => inst.parentInstrument?.instrumentName === selectedInstrument.instrumentName,
          )}
        />
      )}

      {!loading && formType === 'step-level-selection' && <LevelSelection eventBus={eventBus} />}
      {!loading && formType === 'step-level-selection-child' && (
        <MusicJourneySelection eventBus={eventBus} />
      )}
    </>
  );
}
